import { useState, useEffect, useCallback, useMemo } from 'react';
import { Navigate, Link, useSearchParams } from 'react-router-dom';
import { marked } from 'marked';
import { useAuth } from '../hooks/useAuth';
import { Spinner, StatCard } from '../components/common';
import {
  getGraphQLClient,
  GET_AI_EVALUATIONS,
  GET_AI_EVALUATION_DETAIL,
} from '../lib/graphql';

interface AiEvaluation {
  id: string;
  aiPromptLogId: string;
  coherenceScore: number | null;
  engagementScore: number | null;
  relevanceScore: number | null;
  proseQualityScore: number | null;
  depthScore: number | null;
  repetitivenessScore: number | null;
  overallScore: number | null;
  outputWordCount: number | null;
  evaluationModel: string | null;
  evaluationDurationMs: number | null;
  evaluationNotes: string | null;
  criteriaDetails: Record<string, { score: number; rationale: string }> | null;
  errorMessage: string | null;
  success: boolean;
  requestType: string | null;
  bookTitle: string | null;
  modelUsed: string | null;
  logCreatedAt: string | null;
  outputResponse: string | null;
  createdAt: string;
}

interface EvaluationsStats {
  total: number;
  avgOverallScore: number | null;
  avgCoherenceScore: number | null;
  avgEngagementScore: number | null;
  avgRelevanceScore: number | null;
  avgProseQualityScore: number | null;
  avgDepthScore: number | null;
  avgRepetitivenessScore: number | null;
  failedCount: number;
}

interface Book {
  id: string;
  title: string;
}

interface EvaluationsData {
  aiOutputEvaluations: AiEvaluation[];
  aiEvaluationsStats: EvaluationsStats;
  booksWithLogs: Book[];
}

interface EvaluationDetailData {
  aiOutputEvaluation: AiEvaluation | null;
}

const CRITERIA: { key: string; label: string; field: keyof AiEvaluation }[] = [
  { key: 'coherence', label: 'Coherence', field: 'coherenceScore' },
  { key: 'engagement', label: 'Engagement', field: 'engagementScore' },
  { key: 'relevance', label: 'Relevance', field: 'relevanceScore' },
  { key: 'prose_quality', label: 'Prose Quality', field: 'proseQualityScore' },
  { key: 'depth', label: 'Depth', field: 'depthScore' },
  { key: 'repetitiveness', label: 'Repetitiveness', field: 'repetitivenessScore' },
];

function scoreColor(score: number | null): string {
  if (score === null) return 'text-gray-400';
  if (score >= 8) return 'text-green-600 dark:text-green-400';
  if (score >= 6) return 'text-blue-600 dark:text-blue-400';
  if (score >= 4) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
}

function scoreBg(score: number | null): string {
  if (score === null) return 'bg-gray-100 dark:bg-gray-800';
  if (score >= 8) return 'bg-green-50 dark:bg-green-900/20';
  if (score >= 6) return 'bg-blue-50 dark:bg-blue-900/20';
  if (score >= 4) return 'bg-yellow-50 dark:bg-yellow-900/20';
  return 'bg-red-50 dark:bg-red-900/20';
}

function formatScore(score: number | null): string {
  if (score === null) return '-';
  return score.toFixed(1);
}

export default function AiEvaluationsPage() {
  const { user, isLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [evaluations, setEvaluations] = useState<AiEvaluation[]>([]);
  const [stats, setStats] = useState<EvaluationsStats | null>(null);
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedEval, setSelectedEval] = useState<AiEvaluation | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [filterRequestType, setFilterRequestType] = useState<string>('');
  const [filterBookId, setFilterBookId] = useState<string>('');
  const [filterMinScore, setFilterMinScore] = useState<string>('');
  const [filterMaxScore, setFilterMaxScore] = useState<string>('');

  const EVALS_LIMIT = 30;

  const fetchEvaluations = useCallback(async () => {
    if (!user?.admin) return;

    setLoading(true);
    try {
      const client = getGraphQLClient();
      const data = await client.request<EvaluationsData>(GET_AI_EVALUATIONS, {
        requestType: filterRequestType || null,
        bookId: filterBookId || null,
        minScore: filterMinScore ? parseFloat(filterMinScore) : null,
        maxScore: filterMaxScore ? parseFloat(filterMaxScore) : null,
        limit: EVALS_LIMIT,
        offset: 0,
      });

      setEvaluations(data.aiOutputEvaluations);
      setStats(data.aiEvaluationsStats);
      setBooks(data.booksWithLogs);
    } catch (error) {
      console.error('Failed to fetch evaluations:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.admin, filterRequestType, filterBookId, filterMinScore, filterMaxScore]);

  const fetchDetail = useCallback(
    async (evalId: string, updateUrl = true) => {
      setDetailLoading(true);
      try {
        const client = getGraphQLClient();
        const data = await client.request<EvaluationDetailData>(GET_AI_EVALUATION_DETAIL, {
          id: evalId,
        });
        setSelectedEval(data.aiOutputEvaluation);
        if (updateUrl && data.aiOutputEvaluation) {
          setSearchParams({ evalId });
        }
      } catch (error) {
        console.error('Failed to fetch evaluation detail:', error);
      } finally {
        setDetailLoading(false);
      }
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (user?.admin) {
      fetchEvaluations();
    }
  }, [user?.admin, fetchEvaluations]);

  useEffect(() => {
    const evalIdFromUrl = searchParams.get('evalId');
    if (evalIdFromUrl && user?.admin && !selectedEval) {
      fetchDetail(evalIdFromUrl, false);
    } else if (!evalIdFromUrl && selectedEval) {
      setSelectedEval(null);
    }
  }, [searchParams, user?.admin, selectedEval, fetchDetail]);

  const outputHtml = useMemo(() => {
    if (!selectedEval?.outputResponse) return '';
    return marked.parse(selectedEval.outputResponse, { async: false, gfm: true, breaks: true }) as string;
  }, [selectedEval?.outputResponse]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!user?.admin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/admin"
          className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 mb-4"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Admin
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          AI Output Evaluations
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Quality scores for AI-generated prose and expansions
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-4 mb-6">
          <StatCard value={stats.total.toString()} label="Total" />
          <StatCard
            value={formatScore(stats.avgOverallScore)}
            label="Avg Overall"
            color={stats.avgOverallScore && stats.avgOverallScore >= 7 ? 'green' : 'blue'}
          />
          <StatCard value={formatScore(stats.avgCoherenceScore)} label="Coherence" color="blue" />
          <StatCard value={formatScore(stats.avgEngagementScore)} label="Engagement" color="blue" />
          <StatCard value={formatScore(stats.avgRelevanceScore)} label="Relevance" color="blue" />
          <StatCard value={formatScore(stats.avgProseQualityScore)} label="Prose" color="blue" />
          <StatCard value={formatScore(stats.avgDepthScore)} label="Depth" color="blue" />
          <StatCard value={formatScore(stats.avgRepetitivenessScore)} label="Repetitive" color="blue" />
          <StatCard value={stats.failedCount.toString()} label="Failed" color="red" />
        </div>
      )}

      {/* Detail View */}
      {selectedEval && (
        <div className="mb-6 p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <button
            onClick={() => {
              setSelectedEval(null);
              setSearchParams({});
            }}
            className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 mb-4"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to evaluations
          </button>

          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Metadata */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Request Type</div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {selectedEval.requestType?.replace(/_/g, ' ') || '-'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Book</div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {selectedEval.bookTitle || '-'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Words</div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {selectedEval.outputWordCount?.toLocaleString() || '-'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">AI Log</div>
                  <Link
                    to={`/admin/ai-prompt-logs?logId=${selectedEval.aiPromptLogId}`}
                    className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    View log
                  </Link>
                </div>
              </div>

              {/* Overall Score */}
              <div className={`p-4 rounded-lg ${scoreBg(selectedEval.overallScore)}`}>
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Overall Score</div>
                <div className={`text-3xl font-bold ${scoreColor(selectedEval.overallScore)}`}>
                  {formatScore(selectedEval.overallScore)}
                  <span className="text-sm font-normal text-gray-400 ml-1">/ 10</span>
                </div>
              </div>

              {/* Criteria Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {CRITERIA.map(({ key, label, field }) => {
                  const score = selectedEval[field] as number | null;
                  const details = selectedEval.criteriaDetails?.[key];

                  return (
                    <div key={key} className={`p-4 rounded-lg ${scoreBg(score)}`}>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{label}</div>
                      <div className={`text-2xl font-bold ${scoreColor(score)}`}>
                        {formatScore(score)}
                      </div>
                      {details?.rationale && (
                        <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                          {details.rationale}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Notes */}
              {selectedEval.evaluationNotes && (
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Notes</div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    {selectedEval.evaluationNotes}
                  </p>
                </div>
              )}

              {/* Output Text */}
              {selectedEval.outputResponse && (
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                    Evaluated Output
                  </div>
                  <div
                    className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg text-sm overflow-x-auto max-h-96 overflow-y-auto prose dark:prose-invert prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: outputHtml }}
                  />
                </div>
              )}

              {/* Eval Metadata */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-500 dark:text-gray-400">
                <div>
                  <span className="font-medium">Eval Model:</span>{' '}
                  {selectedEval.evaluationModel || '-'}
                </div>
                <div>
                  <span className="font-medium">Eval Duration:</span>{' '}
                  {selectedEval.evaluationDurationMs
                    ? `${selectedEval.evaluationDurationMs.toLocaleString()}ms`
                    : '-'}
                </div>
                <div>
                  <span className="font-medium">Gen Model:</span>{' '}
                  {selectedEval.modelUsed || '-'}
                </div>
                <div>
                  <span className="font-medium">Generated:</span>{' '}
                  {selectedEval.logCreatedAt
                    ? new Date(selectedEval.logCreatedAt).toLocaleString()
                    : '-'}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      {!selectedEval && (
        <div className="flex flex-wrap gap-4 mb-6">
          <select
            value={filterRequestType}
            onChange={(e) => setFilterRequestType(e.target.value)}
            className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Types</option>
            <option value="generate_early_prose">Early Prose</option>
            <option value="topic_based_expansion">Topic Expansion</option>
            <option value="combine_topic_essays">Combined Expansion</option>
          </select>

          <select
            value={filterBookId}
            onChange={(e) => setFilterBookId(e.target.value)}
            className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Books</option>
            {books.map((book) => (
              <option key={book.id} value={book.id}>
                {book.title?.substring(0, 40) || 'Untitled'}
              </option>
            ))}
          </select>

          <select
            value={filterMinScore}
            onChange={(e) => setFilterMinScore(e.target.value)}
            className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Min Score</option>
            <option value="8">8+ (Great)</option>
            <option value="6">6+ (Good)</option>
            <option value="4">4+ (Fair)</option>
            <option value="0">0+ (All)</option>
          </select>

          <select
            value={filterMaxScore}
            onChange={(e) => setFilterMaxScore(e.target.value)}
            className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Max Score</option>
            <option value="4">Under 4</option>
            <option value="6">Under 6</option>
            <option value="8">Under 8</option>
          </select>

          {(filterRequestType || filterBookId || filterMinScore || filterMaxScore) && (
            <button
              onClick={() => {
                setFilterRequestType('');
                setFilterBookId('');
                setFilterMinScore('');
                setFilterMaxScore('');
              }}
              className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* List */}
      {!selectedEval && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : evaluations.length ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Book
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Words
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Overall
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Coh
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Eng
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Rel
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Prose
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Depth
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Rep
                    </th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {evaluations.map((ev) => (
                    <tr key={ev.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {ev.logCreatedAt
                          ? new Date(ev.logCreatedAt).toLocaleDateString()
                          : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs font-medium">
                          {ev.requestType?.replace(/_/g, ' ') || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 max-w-[150px] truncate">
                        {ev.bookTitle || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-right font-mono">
                        {ev.outputWordCount?.toLocaleString() || '-'}
                      </td>
                      <td className={`px-4 py-3 text-sm text-right font-mono font-bold ${scoreColor(ev.overallScore)}`}>
                        {formatScore(ev.overallScore)}
                      </td>
                      <td className={`px-4 py-3 text-sm text-right font-mono ${scoreColor(ev.coherenceScore)}`}>
                        {formatScore(ev.coherenceScore)}
                      </td>
                      <td className={`px-4 py-3 text-sm text-right font-mono ${scoreColor(ev.engagementScore)}`}>
                        {formatScore(ev.engagementScore)}
                      </td>
                      <td className={`px-4 py-3 text-sm text-right font-mono ${scoreColor(ev.relevanceScore)}`}>
                        {formatScore(ev.relevanceScore)}
                      </td>
                      <td className={`px-4 py-3 text-sm text-right font-mono ${scoreColor(ev.proseQualityScore)}`}>
                        {formatScore(ev.proseQualityScore)}
                      </td>
                      <td className={`px-4 py-3 text-sm text-right font-mono ${scoreColor(ev.depthScore)}`}>
                        {formatScore(ev.depthScore)}
                      </td>
                      <td className={`px-4 py-3 text-sm text-right font-mono ${scoreColor(ev.repetitivenessScore)}`}>
                        {formatScore(ev.repetitivenessScore)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <Link
                          to={`/admin/ai-evaluations?evalId=${ev.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            fetchDetail(ev.id);
                          }}
                          className="text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          Details
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              No evaluations found. Evaluations are created automatically when AI generates prose or expansions in development mode.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
